import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CreditCard, CheckCircle2 } from 'lucide-react';
import PaymentForm from './PaymentForm';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  // Get payment data from location state or use defaults
  const paymentData = location.state?.paymentData || {
    amount: 0,
    eventId: '',
    conventionOwnerId: '',
    registrationData: {
      name: '',
      email: '',
      eventName: 'Event Registration',
    }
  };
  
  // Handle successful payment
  const handlePaymentSuccess = (paymentIntent) => {
    setPaymentComplete(true);
    
    // Redirect after a delay
    setTimeout(() => {
      navigate('/registration/confirmation', { 
        state: { 
          paymentIntent,
          registrationData: paymentData.registrationData
        } 
      });
    }, 2000);
  };
  
  // Handle payment error
  const handlePaymentError = (error) => {
    setPaymentError(error.message || 'Payment failed');
  };
  
  // Go back to previous page
  const handleGoBack = () => {
    navigate(-1);
  };
  
  // Validate payment data
  useEffect(() => {
    if (!paymentData.amount || !paymentData.eventId || !paymentData.conventionOwnerId) {
      setPaymentError('Invalid payment data. Please go back and try again.');
    }
  }, [paymentData]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={handleGoBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Complete Your Payment
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {paymentData.registrationData?.eventName || 'Event Registration'}
          </p>
        </div>
        
        {/* Main content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* Order summary */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Order Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Registration Fee</span>
                <span className="text-gray-900 dark:text-white">${paymentData.amount?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">Processing Fee</span>
                <span className="text-gray-900 dark:text-white">$0.00</span>
              </div>
              <div className="flex justify-between py-2 font-medium">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-gray-900 dark:text-white">${paymentData.amount?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
          
          {/* Payment error */}
          {paymentError && !paymentComplete && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
              <p>{paymentError}</p>
            </div>
          )}
          
          {/* Payment complete */}
          {paymentComplete ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 dark:bg-green-900/30">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                Payment Successful!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Thank you for your payment. You will be redirected shortly.
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                Payment Details
              </h3>
              
              {/* Payment form */}
              <PaymentForm 
                amount={paymentData.amount}
                eventId={paymentData.eventId}
                conventionOwnerId={paymentData.conventionOwnerId}
                registrationData={paymentData.registrationData}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </>
          )}
        </div>
        
        {/* Secure payment notice */}
        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>Secure payment powered by Stripe</p>
          <p className="mt-1">Your payment information is encrypted and secure.</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 